import intl from "react-intl-universal";

import type {AppState} from "@stores/AppState";
import {pinItem, unPinItem} from "./pin";
import {updateItemChildIdsSet} from "./updateItem";
import {WorkspaceModelType} from "@stores/WorkspaceStore";
import {isPinned} from "../../utils/isPinned";
import {
    updateWorkspaceArchivedIdsSet,
    updateWorkspaceChildIdsSet,
    updateWorkspaceTrashedIdsSet
} from "../workspace/updateWorkspace";
import {moveToArchive} from "./archive";
import {moveToTrash} from "./trash";
import {UndoableActionGroup} from "../common/UndoableActionGroup";
import {UndoableAction} from "../common/UndoableAction";
import {undoableFunctionCall} from "../common/undoableFunctionCall";
import { CrudServices } from "../../platform/services/types/ServiceDependencies";


export class UndoableMoveItem implements UndoableAction {
    args: Parameters<typeof moveItem>;
    undoableGroup: UndoableActionGroup;
    constructor(...args: Parameters<typeof moveItem>) {
        this.undoableGroup = new UndoableActionGroup();
        this.args = args;
    }
    execute(): Promise<any> {
        /* @ts-ignore the last argument of moveItem is not used in initial constructor and exists only for this use-case */
        return moveItem.apply(null, [...this.args, this.undoableGroup]);
    }
    undo(): Promise<any> {
        return this.undoableGroup.undo();
    }
}


export const moveItemUndoable = (
    services: CrudServices,
    appState: AppState,
    updatePositionResponse: any,
    activeWorkspace: WorkspaceModelType,
    pinnedLength: number,
    sourceIndex: number
) => {
    const undoableAction = new UndoableMoveItem(services, appState, updatePositionResponse, activeWorkspace, pinnedLength, sourceIndex);
    return undoableFunctionCall(undoableAction, intl.get('snackbar.moved').d('Page moved.'));
}

export const moveItem = (
    services: CrudServices,
    appState: AppState,
    {
        localDestinationIndex,
        sourceItem,
        isDestinationTheSame,
        isSourceWorkspaceRoot,
        isDestinationWorkspaceRoot,
        sourceChilds,
        sourceParentItemId,
        destinationChilds,
        destinationParentItemId
    }: any,
    activeWorkspace: WorkspaceModelType,
    pinnedLength: number,
    sourceIndex: number,
    undoableActionGroup?: UndoableActionGroup,
) => {
    const userState = appState.userState!;
    const dsManager = userState.dsManager!;
    let dbChangeQueue: Promise<any> = Promise.resolve();
    if (isDestinationWorkspaceRoot) {
        if (localDestinationIndex >= pinnedLength) {
            dbChangeQueue = updateCorrespondingWorkspaceArray(services, appState, activeWorkspace, destinationChilds, dbChangeQueue, undoableActionGroup);
        } else {
            if (sourceItem.isLeaf) {
                dbChangeQueue = dbChangeQueue.then(() => pinItem(services, dsManager, sourceItem.id, activeWorkspace, undoableActionGroup));
                /* we don't remove items from original location on pin move */
                return dbChangeQueue;
            } else {
                /* ignore move to pin section for cluster items */
                return dbChangeQueue;
            }
        }
    } else {
        dbChangeQueue = updateItemChildIdsSet(services, dsManager, destinationParentItemId!, destinationChilds, activeWorkspace, dbChangeQueue, undoableActionGroup);
    }

    if (!isDestinationTheSame) {
        // we update source childs only if the dist is different otherwise it already included in the dist
        if (isSourceWorkspaceRoot) {
            if (sourceIndex >= pinnedLength) {
                dbChangeQueue = updateCorrespondingWorkspaceArray(services, appState, activeWorkspace, sourceChilds, dbChangeQueue, undoableActionGroup);
            } else {
                if (sourceItem.isLeaf) {
                    dbChangeQueue = dbChangeQueue.then(() => unPinItem(services, dsManager, sourceItem.id, activeWorkspace, true, undoableActionGroup));
                }
            }
        } else {
            dbChangeQueue = updateItemChildIdsSet(services, dsManager, sourceParentItemId!, sourceChilds, activeWorkspace, dbChangeQueue, undoableActionGroup);
        }
    }
    return dbChangeQueue;
}

function updateCorrespondingWorkspaceArray(
    services: CrudServices,
    appState: AppState,
    workspaceMST: WorkspaceModelType, 
    updatedChildIds: string[], 
    dbChangeQueue: Promise<any>,
    undoableActionGroup?: UndoableActionGroup,
) {
    const wsUpdateServices = {...services, dsManager: appState.userState!.dsManager!};
    switch (workspaceMST.view) {
        case 'main':
            let childsWithoutPinned = updatedChildIds.map(itemId => workspaceMST.items.get(itemId)!).filter(item => !isPinned(item)).map(item => item.id);
            return updateWorkspaceChildIdsSet(wsUpdateServices, workspaceMST, childsWithoutPinned, dbChangeQueue, undoableActionGroup);
        case 'archive':
            for (const itemId of updatedChildIds) {
                const item = workspaceMST.items.get(itemId)!;
                if (!item.archived) {
                    dbChangeQueue = dbChangeQueue.then(() => moveToArchive(services, appState, itemId, workspaceMST, 0, undoableActionGroup));
                }
            }
            return updateWorkspaceArchivedIdsSet(wsUpdateServices, workspaceMST, updatedChildIds, dbChangeQueue, undoableActionGroup);
        case 'trash':
            for (const itemId of updatedChildIds) {
                const item = workspaceMST.items.get(itemId)!;
                if (!item.trashed) {
                    dbChangeQueue = dbChangeQueue.then(() => moveToTrash(services, appState, itemId, workspaceMST, 0, undoableActionGroup));
                }
            }
            return updateWorkspaceTrashedIdsSet(wsUpdateServices, workspaceMST, updatedChildIds, dbChangeQueue, undoableActionGroup);
        default:
            throw new Error('not supported state');
    }
}
